import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Header.css';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { getEduPortalUrl } from '../../config/api';
import { isEduPortal, isPathbuddyHost } from '../../config/eduHost';
import { withAppBasename } from '../../config/appBase';
import { isIvoriApexHost } from '../../config/ivoriHost';
import { getExamHostKey } from '../../config/examHost';

const Header = ({ onNavigate, variant }) => {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  const user = authUser || (() => {
    try {
      const raw = localStorage.getItem('pathbuddy_user') || sessionStorage.getItem('pathbuddy_user') || localStorage.getItem('user');
      return raw ? (typeof raw === 'string' && raw.startsWith('{') ? JSON.parse(raw) : { email: raw }) : null;
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignInClick = (e) => {
    e.preventDefault();
    const h = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase();
    const examKey = getExamHostKey(h);

    if (examKey) {
      window.location.href = getEduPortalUrl(`/login?redirect=/practice/${examKey}`);
    } else {
      navigate('/login');
    }
  };

  const handleLogoClick = () => {
    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = withAppBasename('/');
    }
  };

  const handleSignUpClick = (e) => {
    e.preventDefault();
    const pricingElem = document.getElementById('pricing') || document.querySelector('.tmua-pricing-section') || document.querySelector('.lnat-pricing-section') || document.querySelector('.ucat-pricing-section');
    if (pricingElem) {
      pricingElem.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/login?signup=true');
    }
  };

  const hostname = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase();
  const pathname = (location?.pathname || '').toLowerCase();
  const examHostKey = getExamHostKey(hostname);
  const onPathbuddy = isPathbuddyHost(hostname);

  // Check if current route is a specialty exam landing route
  const isExamRoute = (
    pathname.startsWith('/lnat') ||
    pathname.startsWith('/tmua') ||
    pathname.startsWith('/ucat') ||
    pathname.startsWith('/esat') ||
    pathname.startsWith('/tara') ||
    pathname.startsWith('/tsa')
  );

  // Transparent header is ONLY allowed on edu portal (ivoriai.com/edu, legacy edu hosts, pathbuddy.com) and apex ivori.ai homepage hero
  // It is NEVER transparent for exam subdomains (lnat.ivori.ai, etc.) or specialty landing pages
  const isEduDomain = (isEduPortal(hostname) || hostname.includes('edu.') || hostname.startsWith('edu')) && !examHostKey;
  const isApexIvori = (isIvoriApexHost(hostname) || hostname === 'localhost' || hostname === '127.0.0.1') && !examHostKey;

  const isTransparentAllowed = Boolean(
    (isEduDomain || isApexIvori) &&
    !isExamRoute &&
    !examHostKey &&
    (variant === 'edu' || variant === 'transparent' || (variant === 'landing' && (pathname === '/' || pathname === '/edu')))
  );

  const isExamLanding = variant === 'exam-landing';

  const headerClass = isExamLanding
    ? 'nav nav-exam-landing'
    : isTransparentAllowed
      ? `nav nav-transparent ${isScrolled ? 'header-scrolled' : ''}`
      : 'nav nav-solid-light';

  const brandLabel = onPathbuddy ? 'Pathbuddy' : 'ivori';
  const brandAria = onPathbuddy ? 'Pathbuddy home' : 'ivori home';

  return (
    <header
      id="header"
      className={`${headerClass} ${
        ['/lnat', '/tmua', '/esat', '/ucat'].some(path =>
          pathname.startsWith(path)
        )
          ? 'nav-exam-landing'
          : ''
      }`}
    >
      <div className="nav-inner">
        <a
          className="logo"
          href={withAppBasename('/')}
          aria-label={brandAria}
          onClick={(e) => {
            e.preventDefault();
            handleLogoClick();
          }}
        >
          {variant === 'exam-landing' ? (
            <span className="exam-landing-brand">
              {onPathbuddy ? (
                'Pathbuddy'
              ) : (
                <>
                  IVORI <span>Edu</span>
                </>
              )}
            </span>
          ) : (
            <>
              <img
                src="/logo-yellow.png"
                alt={onPathbuddy ? 'Pathbuddy Logo' : 'ivori Logo'}
                style={{
                  height: '32px',
                  width: 'auto',
                  display: 'block',
                  objectFit: 'contain'
                }}
              />
              <span
                className="nav-brand-text"
                style={{
                  display: 'inline-flex',
                  alignItems: 'baseline',
                  transition: 'color 0.25s ease'
                }}
              >
                {brandLabel}
                {!onPathbuddy && (isEduDomain || isExamRoute) && (
                  <span className="brand-sub-tag">Edu</span>
                )}
              </span>
            </>
          )}
        </a>

        <div className="nav-actions" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <a className="btn btn-secondary-nav" href={withAppBasename('/login')} onClick={handleSignInClick}>
            Sign In
          </a>
          <a className="btn btn-navy" href="#pricing" onClick={handleSignUpClick}>
            {onPathbuddy ? 'Try Pathbuddy' : 'Try ivori'}
          </a>
        </div>
      </div>
    </header>
  );
};

export default Header;